import React, { useState, useRef, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  message,
  Switch,
  Space,
  Avatar,
  InputNumber,
  Popconfirm,
  Tag,
  Row,
  Col,
  Radio,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";

import moment from "moment";

import {
  getKaryawan,
  createKaryawan,
  updateKaryawan,
  deleteKaryawan,
} from "../../api/master/Karyawan";
import { dropdownRoles } from "../../api/setting/Role";
import { getDropdownJabatan } from "../../api/master/Jabatan";

const { Option } = Select;

const formatRupiah = (angka) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(angka);
};

const KaryawanPage = () => {
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const [dataKaryawan, setDataKaryawan] = useState([]);
  const [editingKaryawan, setEditingKaryawan] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const [roleList, setRoleList] = useState([]);
  const [jabatanList, setjabatanList] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchRoleList();
    fetchJabatanList();
  }, []);

  const fetchUsers = async () => {
    setIsLoadingTable(true);
    try {
      const response = await getKaryawan();
      const result = Array.isArray(response?.data) ? response.data : [];
      setDataKaryawan(result);
    } catch (error) {
      message.error(`Gagal memuat data karyawan: ${error}`);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const fetchRoleList = async () => {
    try {
      const rolesData = await dropdownRoles();
      setRoleList(Array.isArray(rolesData.data) ? rolesData.data : []);
    } catch (error) {
      message.error(`Failed to fetch roles: ` + error);
    }
  };

  const fetchJabatanList = async () => {
    try {
      const jabatanData = await getDropdownJabatan();

      const list = Array.isArray(jabatanData)
        ? jabatanData
        : Array.isArray(jabatanData?.data)
        ? jabatanData.data
        : [];
      setjabatanList(list);
    } catch (error) {
      message.error(`Gagal memuat data jabatan: ${error}`);
    }
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Cari ${title}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Cari
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() || ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleAdd = () => {
    setEditingKaryawan(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    form.resetFields();
    setEditingKaryawan(record);
    form.setFieldsValue({
      ...record,
      jabatanId: record.jabatanDetails?.jbt_id_jabatan,
      empRole: record.roleDetail?.rl_id_role,
      statusAkun: record.userDetail?.usr_status === "Aktif" ? true : false,
      status: record.emp_status_karyawan === "Aktif" ? true : false,
      emp_gender: record.emp_gender,
      emp_tanggal_lahir: moment(record.emp_tanggal_lahir),
      emp_tanggal_masuk: record.emp_tanggal_masuk
        ? moment(record.emp_tanggal_masuk)
        : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    setIsLoadingSubmit(true);
    try {
      await deleteKaryawan(id);
      setIsLoadingSubmit(false);
      message.success("Karyawan berhasil dihapus");
      fetchUsers();
    } catch (err) {
      setIsLoadingSubmit(false);
      message.error("Gagal hapus karyawan: " + err);
    }
  };

  const handleFinish = async (values) => {
    setIsLoadingSubmit(true);
    const formData = new FormData();
    formData.append("empNip", values.emp_nip);
    formData.append("empNama", values.emp_nama);
    formData.append("empNpwp", values.emp_npwp);
    formData.append("empGender", values.emp_gender);
    formData.append("empAlamat", values.emp_alamat);
    formData.append(
      "empTanggalLahir",
      values.emp_tanggal_lahir.format("YYYY-MM-DD")
    );
    formData.append("empEmail", values.emp_email);
    formData.append("empNoTelepon", values.emp_no_telepon);
    formData.append("empJabatan", values.jabatanId);
    formData.append(
      "empTanggalMasuk",
      values.emp_tanggal_masuk?.format("YYYY-MM-DD")
    );
    formData.append(
      "empStatusKaryawan",
      values.status ? "Aktif" : "Tidak Aktif"
    );
    formData.append(
      "empStatusAkun",
      values.statusAkun ? "Aktif" : "Tidak Aktif"
    );

    formData.append("empGajiPokok", values.emp_gaji_pokok);
    formData.append("empCreatedBy", sessiData[0].user[0].fullname);
    formData.append("empRole", values.empRole);

    if (!editingKaryawan) {
      // tanpa image upload
      try {
        formData.append("userName", values.emp_email);
        await createKaryawan(Object.fromEntries(formData));
        message.success("Karyawan berhasil ditambahkan");
        fetchUsers();
      } catch (err) {
        message.error("Gagal tambah karyawan: " + err);
      } finally {
        setIsLoadingSubmit(false);
      }
    } else {
      if (values.emp_photo?.file) {
        formData.append("image", values.emp_photo.file);
      }
      try {
        await updateKaryawan(editingKaryawan.emp_id_karyawan, formData);
        message.success("Karyawan berhasil diperbarui");
        fetchUsers();
      } catch (err) {
        message.error("Gagal update karyawan: " + err);
      } finally {
        setIsLoadingSubmit(false);
      }
    }
    setModalVisible(false);
    form.resetFields();
  };

  const handleJabatanChange = (jabatanId) => {
    const jabatan = jabatanList.find((j) => j.jbt_id_jabatan === jabatanId);
    if (jabatan) {
      form.setFieldsValue({ emp_gaji_pokok: jabatan.jbt_gaji_pokok_minimum });
    }
  };

  const columns = [
    {
      title: "Avatar",
      dataIndex: "emp_photo",
      key: "emp_photo",
      fixed: "left",
      render: (emp_photo) => <Avatar src={emp_photo} />,
    },
    {
      title: "NIP",
      dataIndex: "emp_nip",
      key: "emp_nip",
      fixed: "left",
      ...getColumnSearchProps("emp_nip", "NIP"),
    },
    {
      title: "Nama",
      dataIndex: "emp_nama",
      key: "emp_nama",
      fixed: "left",
      ...getColumnSearchProps("emp_nama", "Nama"),
    },
    {
      title: "NPWP",
      dataIndex: "emp_npwp",
      key: "emp_npwp",
      ...getColumnSearchProps("emp_npwp", "NPWP"),
    },
    {
      title: "Alamat",
      dataIndex: "emp_alamat",
      key: "emp_alamat",
    },
    {
      title: "Tanggal Lahir",
      key: "emp_tanggal_lahir",
      render: (_, record) =>
        record.emp_tanggal_lahir
          ? moment(record.emp_tanggal_lahir).format("DD MMMM YYYY")
          : "-",
    },
    {
      title: "Email",
      dataIndex: "emp_email",
      key: "emp_email",
    },
    {
      title: "Telepon",
      dataIndex: "emp_no_telepon",
      key: "emp_no_telepon",
    },
    {
      title: "Jabatan",
      dataIndex: ["jabatanDetails", "jbt_nama_jabatan"],
      key: "jabatan",
      filters: jabatanList.map((j) => ({
        text: j.jbt_nama_jabatan.trim(),
        value: j.jbt_nama_jabatan.trim(),
      })),
      onFilter: (value, record) =>
        record.jabatanDetails?.jbt_nama_jabatan.trim() === value,
    },

    {
      title: "Gaji Pokok",
      dataIndex: "emp_gaji_pokok",
      key: "emp_gaji_pokok",
      render: (gaji) => formatRupiah(gaji),
    },
    {
      title: "Gender",
      dataIndex: "emp_gender",
      key: "emp_gender",
    },
    {
      title: "Status Karyawan",
      key: "emp_status_karyawan",
      render: (_, record) =>
        record.emp_status_karyawan == "Aktif" ? (
          <Tag color="green">Aktif</Tag>
        ) : (
          <Tag color="red">Tidak Aktif</Tag>
        ),
    },
    {
      title: "Status Akun",
      key: "userDetail.usr_status",
      render: (_, record) =>
        record.userDetail?.usr_status == "Aktif" ? (
          <Tag color="green">Aktif</Tag>
        ) : (
          <Tag color="red">Tidak Aktif</Tag>
        ),
    },
    {
      title: "Role",
      key: "roleDetail.rl_nama_role",
      render: (_, record) => record.roleDetail?.rl_nama_role,
    },
    {
      title: "Tanggal Masuk",
      key: "emp_tanggal_masuk",
      render: (_, record) =>
        record.emp_tanggal_masuk
          ? moment(record.emp_tanggal_masuk).format("DD MMMM YYYY")
          : "-",
    },
    {
      title: "Aksi",
      key: "aksi",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)} type="link">
            Edit
          </Button>
          <Popconfirm
            title="Apakah Anda yakin ingin menghapus karyawan ini?"
            onConfirm={() => handleDelete(record.emp_id_karyawan)}
            okText="Ya"
            cancelText="Batal"
          >
            <Button danger type="link">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ marginBottom: 16 }}
      >
        Tambah Karyawan
      </Button>

      <Table
        loading={isLoadingTable}
        dataSource={dataKaryawan}
        columns={columns}
        rowKey="emp_id_karyawan"
        scroll={{ x: "max-content" }}
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
      />

      <Modal
        title={editingKaryawan ? "Edit Karyawan" : "Tambah Karyawan"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={isLoadingSubmit}
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            name="emp_nip"
            label="NIP"
            rules={[{ required: true, message: "Masukkan NIP" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="emp_npwp"
            label="NPWP"
            rules={[{ message: "Masukkan NPWP" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="emp_nama" label="Nama" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="emp_alamat"
            label="Alamat"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="emp_tanggal_lahir"
            label="Tanggal Lahir"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="emp_email"
            label="Email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="emp_no_telepon"
            label="Telepon"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="jabatanId"
            label="Jabatan"
            rules={[{ required: true, message: "Pilih jabatan" }]}
          >
            <Select placeholder="Pilih jabatan" onChange={handleJabatanChange}>
              {Array.isArray(jabatanList) &&
                jabatanList.map((j) => (
                  <Option key={j.jbt_id_jabatan} value={j.jbt_id_jabatan}>
                    {j.jbt_nama_jabatan.trim()}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="emp_gaji_pokok"
            label="Gaji Pokok"
            rules={[{ required: true }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              formatter={(value) => formatRupiah(value)}
              parser={(value) => value.replace(/[Rp\s.]+/g, "")}
            />
          </Form.Item>
          <Form.Item
            name="emp_tanggal_masuk"
            label="Tanggal Masuk Kerja"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="empRole" label="Role" rules={[{ required: true }]}>
            <Select placeholder="Pilih Role" allowClear>
              {roleList?.length > 0 &&
                roleList.map((role) => (
                  <Option key={role.rl_id_role} value={role.rl_id_role}>
                    {role.rl_nama_role}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="emp_gender"
                label="Jenis Kelamin"
                rules={[{ required: true, message: "Pilih jenis kelamin" }]}
              >
                <Radio.Group>
                  <Radio value="Pria">Pria</Radio>
                  <Radio value="Wanita">Wanita</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status Karyawan"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Aktif"
                  unCheckedChildren="Tidak Aktif"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="statusAkun"
                label="Status Akun"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Aktif"
                  unCheckedChildren="Tidak Aktif"
                />
              </Form.Item>
            </Col>
          </Row>

          {editingKaryawan && (
            <Form.Item name="emp_photo" label="Upload Foto">
              <Upload
                maxCount={1}
                listType="picture"
                beforeUpload={() => false}
              >
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default KaryawanPage;
